<script setup lang="ts">
import { useStore } from "@/store/Store";
import { EventBus } from "@/utils/EventBus";
import { computed, onMounted, ref } from "vue";
import Modal from "./player/Modal.vue";
import PaceInput from "./player/PaceInput.vue";
import UnitToggle from "./UnitToggle.vue";
import { KM_TO_MILES, KM_TO_100M, PACE_100M_TO_100YD } from "@/Util";
import { generatePacePdf, type PdfLegData, type PdfPaceData } from "@/utils/PacePdfExport";

const store = useStore()

const visible = ref(false)

// Unit toggle state - initialized once from map setting, then preserved across modal opens
const unitInitialized = ref(false)
const unit = ref<'km' | 'mi'>('km')

onMounted(() => {
	EventBus.on("paceCalculator", () => {
		// Only initialize unit from map setting on first open
		if (!unitInitialized.value) {
			unit.value = store.map?.unit === 'mi' ? 'mi' : 'km'
			unitInitialized.value = true
		}
		visible.value = true;
	})
})

const useMiles = computed(() => unit.value === 'mi')

const uniqueTypes = computed(() => {
	const allColors = store.trackFeatureLegs.map(leg => leg.properties.color)
	return [...new Set(allColors.map(color => store.trackFeature.properties.paceCalculation.groups[color]?.type ?? "running"))]
})

// Paces are stored internally in metric (min/km for running/cycling/other, min/100m for swimming)
const paces = ref({
	"running": 5,
	"cycling": 3,
	"swimming": 2.5,
	"other": 5,
	undefined: 5,
})

// Convert metric pace to display pace based on unit and activity type
const toDisplayPace = (metricPace: number, type: string): number => {
	if (!useMiles.value) return metricPace
	// For imperial: convert min/km to min/mi (multiply by conversion factor)
	// For swimming: convert min/100m to min/100yd (100yd is shorter, so pace is faster)
	if (type === 'swimming') {
		return Math.round(metricPace * PACE_100M_TO_100YD * 10) / 10
	}
	// min/km to min/mi: multiply by KM_TO_MILES (it takes longer to run a mile)
	return Math.round(metricPace * KM_TO_MILES * 10) / 10
}

// Convert display pace to metric pace for internal storage
const toMetricPace = (displayPace: number, type: string): number => {
	if (!useMiles.value) return displayPace
	if (type === 'swimming') {
		return displayPace / PACE_100M_TO_100YD
	}
	// min/mi to min/km: divide by KM_TO_MILES
	return displayPace / KM_TO_MILES
}

// Get pace unit label based on activity type and current unit setting
const getPaceUnit = (type: string): string => {
	if (type === 'swimming') {
		return useMiles.value ? 'min/100yd' : 'min/100m'
	}
	return useMiles.value ? 'min/mi' : 'min/km'
}

// Format distance based on unit setting
const formatDistance = (lengthKm: number): string => {
	if (useMiles.value) {
		return (lengthKm / KM_TO_MILES).toFixed(2) + ' mi'
	}
	return lengthKm.toFixed(2) + ' km'
}

// Start time in decimal hours (e.g., 14.5 = 14:30), null when empty
const startTime = ref<number | null>(null)

const typeLabels: Record<string, string> = {
	'running': 'Run',
	'swimming': 'Swim',
	'cycling': 'Cycle',
	'other': 'Other',
}

const calculatedLegs = computed(() => {
	let totalTime = 0
	const typeCounts: Record<string, number> = {}

	return store.trackFeatureLegs.map(leg => {
		const type = store.trackFeature.properties.paceCalculation.groups[leg.properties.color]?.type || "running"
		typeCounts[type] = (typeCounts[type] || 0) + 1
		const legNumber = typeCounts[type]
		const distance = leg.properties.length ?? store.actualTrackLength

		const pace = paces.value[type] || 0
		// Time calculation always uses metric internally:
		// - Swimming: km * 10 (100m segments) * pace (min/100m)
		// - Other: km * pace (min/km)
		// Display unit only affects how distance/pace are shown, not the actual time
		const sectionTime = type === 'swimming'
			? distance * KM_TO_100M * pace
			: distance * pace
		totalTime += sectionTime

		const label = typeLabels[type] || "Leg"

		return {
			leg,
			distance,
			type,
			sectionTime,
			totalTime,
			legNumber,
			displayName: `${label} ${legNumber}`
		}
	})
})

const totalDistance = computed(() =>
	calculatedLegs.value.reduce((sum, item) => sum + item.distance, 0)
)

const totalElapsedTime = computed(() =>
	calculatedLegs.value.length > 0
		? calculatedLegs.value[calculatedLegs.value.length - 1].totalTime
		: 0
)

const modeTotals = computed(() => {
	const totals: Record<string, { type: string, distance: number, time: number, label: string }> = {}
	for (const item of calculatedLegs.value) {
		if (!totals[item.type]) {
			totals[item.type] = { type: item.type, distance: 0, time: 0, label: typeLabels[item.type] || 'Leg' }
		}
		totals[item.type].distance += item.distance
		totals[item.type].time += item.sectionTime
	}
	return Object.values(totals)
})

const formatTime = (minutes: number): string => {
	const totalSeconds = Math.round(minutes * 60)
	const hours = Math.floor(totalSeconds / 3600)
	const mins = Math.floor((totalSeconds % 3600) / 60)
	const secs = totalSeconds % 60

	if (hours > 0) {
		return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
	}
	return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Format clock time from decimal hours to HH:MM:SS
// Optionally add minutes to the clock time before formatting
const formatClockTime = (decimalHours: number, minutesToAdd: number = 0): string => {
	const adjustedDecimalHours = decimalHours + (minutesToAdd / 60)
	const totalSeconds = adjustedDecimalHours * 3600
	const hours = Math.floor(totalSeconds / 3600) % 24
	const minutes = Math.floor((totalSeconds % 3600) / 60)
	const seconds = Math.round(totalSeconds % 60)
	return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

// PDF Export
const pdfLoading = ref(false)

const exportPdf = async () => {
	pdfLoading.value = true
	try {
		// Build leg data for PDF
		const legs: PdfLegData[] = calculatedLegs.value.map(item => ({
			displayName: item.displayName,
			distance: formatDistance(item.leg.properties.length),
			sectionTime: formatTime(item.sectionTime),
			totalTime: startTime.value !== null
				? formatClockTime(startTime.value, item.totalTime)
				: formatTime(item.totalTime)
		}))

		// Build paces data for PDF
		const pacesData: PdfPaceData[] = uniqueTypes.value.map(type => ({
			type: type ?? 'Leg',
			pace: formatTime(toDisplayPace(paces.value[type], type)),
			unit: getPaceUnit(type)
		}))

		// Calculate total elapsed time
		const totalElapsedTimeFormatted = calculatedLegs.value.length > 0
			? formatTime(totalElapsedTime.value)
			: null

		await generatePacePdf({
			mapName: store.map?.name || 'Pace Calculator',
			routeLabel: store.currentRoute?.properties?.label || '',
			legs,
			paces: pacesData,
			startTime: startTime.value !== null ? formatClockTime(startTime.value) : null,
			totalElapsedTime: totalElapsedTimeFormatted,
			totalDistance: calculatedLegs.value.length > 0 ? formatDistance(totalDistance.value) : null,
			modeTotals: modeTotals.value.map(mt => ({
				label: mt.label,
				distance: formatDistance(mt.distance),
				time: formatTime(mt.time),
			})),
			generatedAt: new Date()
		})
	} finally {
		pdfLoading.value = false
	}
}

</script>

<template>
	<Modal :show="visible" closeable @close="visible = false">

		<div class="header-row">
			<h2>Pace Calculator</h2>
			<UnitToggle v-model="unit" />
		</div>

		<div class="paces">
			<div class="pace-inputs">
				<PaceInput v-for="(type, i) in uniqueTypes" :key="i" :label="(type ?? 'Leg') + ' pace'"
					:unit="getPaceUnit(type)" :model-value="toDisplayPace(paces[type], type)"
					@update:model-value="paces[type] = toMetricPace($event ?? 0, type)" />
			</div>
			<div class="start-time" v-if="store.trackFeature.properties.paceCalculation?.clockTime">
				<PaceInput label="Start Time" unit="" v-model="startTime" :is-clock-time="true" placeholder="14:30" />
			</div>
		</div>

		<div class="table-container">
			<table class="pace-table">
				<thead>
					<tr>
						<th>Leg</th>
						<th>Distance</th>
						<th>Time</th>
						<th>{{ startTime !== null ? 'Clock' : 'Elapsed' }}</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(item, i) in calculatedLegs" :key="item.leg.id">
						<td class="leg-name">{{ item.displayName }}</td>
						<td>{{ formatDistance(item.distance) }}</td>
						<td>{{ formatTime(item.sectionTime) }}</td>
						<td class="total-time">{{ startTime !== null ? formatClockTime(startTime, item.totalTime) :
							formatTime(item.totalTime) }}</td>
					</tr>
				</tbody>
				<tfoot v-if="calculatedLegs.length > 0">
					<tr class="total-row">
						<td>Total</td>
						<td>{{ formatDistance(totalDistance) }}</td>
						<td></td>
						<td class="total-time">{{ formatTime(totalElapsedTime) }}</td>
					</tr>
					<tr v-for="mt in modeTotals.length > 1 ? modeTotals : []" :key="mt.type" class="mode-total-row">
						<td>Total {{ mt.label }}</td>
						<td>{{ formatDistance(mt.distance) }}</td>
						<td></td>
						<td class="total-time">{{ formatTime(mt.time) }}</td>
					</tr>
				</tfoot>
			</table>
		</div>

		<div class="actions">
			<button class="pdf-button" @click="exportPdf" :disabled="pdfLoading">
				<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
					<path fill="currentColor" d="M4 22v-2h16v2zm8-4L5 9h4V2h6v7h4z" />
				</svg>
				{{ pdfLoading ? 'Generating...' : 'Download PDF' }}
			</button>
		</div>
	</Modal>
</template>


<style scoped>
.header-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 1.25rem;
	padding-bottom: 1rem;
	border-bottom: 1px solid #eee;
	gap: 1rem;
	width: 100%;

	h2 {
		margin: 0;
		font-size: 1.1rem;
		font-weight: 600;
		color: #333;
		text-transform: none;
	}
}

.paces {
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1rem 1.25rem;
	background: #f0f0f0;
	border-radius: 8px;
	width: 100%;
}

.pace-inputs {
	display: flex;
	flex-wrap: wrap;
	gap: 1.25rem;
}

.start-time {
	display: flex;
	padding-top: 0.75rem;
	border-top: 1px dashed #ddd;
}

.table-container {
	width: calc(100% + 3rem);
	margin-left: -1.5rem;
	margin-right: -1.5rem;
	overflow-x: auto;
	overflow-y: visible;
	margin-top: 1.25rem;
	flex: 0 0 auto;
}

.pace-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 0.9rem;
}

.pace-table thead {
	border-bottom: 1px solid #ddd;
}

.pace-table th {
	padding: 0.75rem 0.5rem;
	text-align: left;
	font-weight: 500;
	color: #888;
	font-size: 0.8rem;
	text-transform: uppercase;
	letter-spacing: 0.03em;
}

.pace-table td {
	padding: 0.75rem 0.5rem;
	border-bottom: 1px solid #f0f0f0;
	color: #444;
}

.pace-table th:first-child,
.pace-table td:first-child {
	padding-left: 1.5rem;
}

.pace-table th:last-child,
.pace-table td:last-child {
	padding-right: 1.5rem;
}

.pace-table td:nth-child(n+2),
.pace-table th:nth-child(n+2) {
	text-align: right;
}

.pace-table td.leg-name {
	font-weight: 500;
	color: #333;
}

.pace-table tfoot td {
	border-top: 1px solid #ddd;
	border-bottom: none;
	background: #fafafa;
}

.pace-table .total-row td:first-child {
	font-weight: 500;
	color: #666;
	font-size: 0.85rem;
}

.pace-table .mode-total-row td:first-child {
	font-weight: 500;
	color: #666;
	font-size: 0.85rem;
	padding-left: 2rem;
}

.pace-table .total-row td.total-time,
.pace-table .mode-total-row td.total-time {
	font-weight: 600;
	color: #333;
}

.pace-table tbody tr:hover {
	background-color: #fafafa;
}

.actions {
	display: flex;
	justify-content: flex-end;
	margin-top: 1.25rem;
	padding-top: 1rem;
	/* border-top: 1px solid #eee; */
}

.pdf-button {
	font-size: 0.85rem;
	cursor: pointer;
	border: 1px solid #ddd;
	font-family: unset;
	background-color: #fff;
	color: #333;
	padding: 0.5rem 0.75rem;
	border-radius: 6px;
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
	text-decoration: none;
	transition: all 0.15s ease;

	svg {
		width: 1em;
		height: 1em;
		opacity: 0.7;
	}

	&:hover {
		background-color: #f5f5f5;
		border-color: #ccc;
	}

	&:disabled {
		opacity: 0.6;
		cursor: wait;
	}
}
</style>